import { useCallback, useEffect, useRef } from 'react'
import { useAtom, useAtomValue } from 'jotai'
import {
    videoBannersAtom,
    isLoadingVideoBannersAtom,
    videoBannersErrorAtom,
    hasLoadedVideoBannersAtom,
    heroBannerAtom,
    introduceBannerAtom
} from '@/core/store/videoBanner.store'
import { fetchVideoBanners } from '@/core/services/videoBanner.service'
import { RetryGuard } from '@repo/unicity/guard'

// Module-level flag to prevent double fetch across all instances
let isFetchingGlobal = false
// Guard rail: on error the catch resets hasFetchedRef so the effect re-fires — cap
// that to 3 consecutive failures so a persistently-failing endpoint stops hammering.
const retryGuard = new RetryGuard(3)

export const useVideoBanners = () => {
    const [banners, setBanners] = useAtom(videoBannersAtom)
    const [isLoading, setIsLoading] = useAtom(isLoadingVideoBannersAtom)
    const [error, setError] = useAtom(videoBannersErrorAtom)
    const [hasLoaded, setHasLoaded] = useAtom(hasLoadedVideoBannersAtom)
    const heroBanner = useAtomValue(heroBannerAtom)
    const introduceBanner = useAtomValue(introduceBannerAtom)
    const hasFetchedRef = useRef(false)

    const fetchBanners = useCallback(async () => {
        if (hasLoaded || isLoading || isFetchingGlobal || hasFetchedRef.current || retryGuard.exhausted) return

        isFetchingGlobal = true
        hasFetchedRef.current = true
        setIsLoading(true)
        setError(null)

        try {
            const data = await fetchVideoBanners()
            setBanners(data)
            setHasLoaded(true)
            retryGuard.succeed()
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to fetch banners'
            setError(errorMessage)
            retryGuard.fail()
            // Allow another auto-attempt only while under the failure cap; once exhausted,
            // leave the fetched flag set so the effect stops re-firing.
            if (!retryGuard.exhausted) hasFetchedRef.current = false
        } finally {
            setIsLoading(false)
            isFetchingGlobal = false
        }
    }, [hasLoaded, isLoading, setIsLoading, setBanners, setError, setHasLoaded])

    useEffect(() => {
        if (!hasLoaded && !isLoading && !isFetchingGlobal) {
            fetchBanners()
        }
    }, [hasLoaded, isLoading, fetchBanners])

    return {
        banners,
        isLoading,
        error,
        hasLoaded,
        heroBanner,
        introduceBanner,
        fetchBanners
    }
}
